import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Bot, Smartphone, Globe2, Sparkles, Bell, ShoppingBag, TrendingUp, Star } from "lucide-react";

const COMPARE_IMG =
  "https://customer-assets.emergentagent.com/job_fintech-flow-18/artifacts/074aas6u_ChatGPT%20Image%20Apr%2025%2C%202026%2C%2001_34_09%20PM.png";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero({ onPrimaryCta, onChatCta }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-24 pb-20 lg:pt-28 lg:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[95%] h-[70%] radial-glow-violet blur-3xl opacity-80" />
      <div className="absolute -bottom-20 right-0 w-[40%] h-[60%] radial-glow-cyan blur-3xl opacity-60" />

      <Smartphone className="hidden lg:block absolute top-32 left-10 w-7 h-7 text-blue-400/60 animate-floaty-slow" />
      <Globe2 className="hidden lg:block absolute top-56 right-14 w-8 h-8 text-violet-400/60 animate-floaty" />
      <Sparkles className="hidden lg:block absolute bottom-32 left-24 w-6 h-6 text-pink-400/60 animate-floaty-slow" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.h1
          {...fadeUp(0.08)}
          data-testid="hero-heading"
          className="font-display font-extrabold text-center text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl mx-auto"
        >
          Bring Your Business{" "}
          <span className="text-gradient">Online</span>{" "}
          <br className="hidden sm:block" />
          and Grow <span className="text-gradient-neon">Faster</span>.
        </motion.h1>

        <motion.p
          {...fadeUp(0.18)}
          data-testid="hero-subheading"
          className="mt-6 text-center text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed"
        >
          <span className="text-white font-semibold">Website</span>
          {" • "}
          <span className="text-white font-semibold">App</span>
          {" • "}
          <span className="text-white font-semibold">AI Automation</span>
          <br />
          Everything your shop needs to sell online — live in 2 days, starting ₹9,999.
        </motion.p>

        {/* Before / After illustration — moved up so it sits right under the heading */}
        <motion.div
          {...fadeUp(0.25)}
          className="mt-10 relative"
          data-testid="hero-preview"
        >
          <div className="relative mx-auto max-w-5xl rounded-3xl glass-strong border-grad p-3 sm:p-4 overflow-hidden">
            <div className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-[#0a0a1a]">
              <img
                src={COMPARE_IMG}
                alt="Shop transformation: closed offline store becomes a vibrant online business"
                className="w-full h-full object-cover"
                data-testid="hero-compare-image"
                loading="eager"
              />
              {/* Subtle floating sparkles only — image is content-rich on its own */}
              <div className="pointer-events-none absolute inset-0">
                {[
                  { top: "12%", left: "62%", delay: 0 },
                  { top: "26%", left: "84%", delay: 0.5 },
                  { top: "58%", left: "94%", delay: 1.0 },
                  { top: "82%", left: "70%", delay: 1.5 },
                  { top: "44%", left: "55%", delay: 0.7 },
                ].map((p, i) => (
                  <Sparkles
                    key={i}
                    className="absolute w-4 h-4 text-white/70 animate-pulse"
                    style={{ top: p.top, left: p.left, animationDelay: `${p.delay}s` }}
                  />
                ))}
              </div>

              {/* Soft "Live · synced" badge anchored to top-right (does not duplicate any image text) */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.5 }}
                className="absolute top-3 right-3 sm:top-4 sm:right-4"
                data-testid="hero-overlay-live"
              >
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full glass-strong border border-emerald-400/40 shadow-lg shadow-emerald-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] uppercase tracking-widest text-emerald-200 font-semibold">Live · synced</span>
                </div>
              </motion.div>
            </div>

            {/* Metric strip */}
            <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { k: "Setup time", v: "2 days" },
                { k: "Reach", v: "10× more" },
                { k: "Revenue", v: "3× avg" },
              ].map((s, i) => (
                <div
                  key={s.k}
                  className="p-2.5 sm:p-3 rounded-xl bg-white/[0.03] border border-white/5 text-center sm:text-left"
                  data-testid={`hero-metric-${i}`}
                >
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">{s.k}</p>
                  <p className="mt-0.5 font-display text-base sm:text-xl font-bold text-gradient">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-20 -bottom-4 h-10 bg-violet-500/30 blur-3xl rounded-full" />
        </motion.div>

        <motion.div
          {...fadeUp(0.38)}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={onPrimaryCta}
            className="group w-full sm:w-auto px-7 py-3.5 rounded-full btn-primary-grad text-white font-semibold text-base transition-all hover:-translate-y-0.5 shadow-2xl shadow-violet-500/40 flex items-center justify-center gap-2"
          >
            Get Started
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>

          <button
            data-testid="hero-chat-cta"
            onClick={onChatCta}
            className="w-full sm:w-auto px-7 py-3.5 rounded-full glass hover:bg-white/10 text-white font-semibold text-base transition-all flex items-center justify-center gap-2 border border-white/10"
          >
            <Bot className="w-4 h-4 text-violet-300" />
            Chat with AI
          </button>
        </motion.div>

        <motion.div
          {...fadeUp(0.46)}
          className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-xs text-slate-400"
          data-testid="hero-trust"
        >
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Live in 2 days
          </span>
          <span className="hidden sm:inline text-white/20">·</span>
          <span>Low cost. High ROI.</span>
          <span className="hidden sm:inline text-white/20">·</span>
          <span>Hindi + English support</span>
        </motion.div>
      </div>
    </section>
  );
}
